// frontend/src/components/entry/StoreSetup.jsx
//
// The install setup step, as a popup in front of the app (`entryScreen.js`
// puts it after the claim question and before billing): how the store's
// products are grouped into categories, where its suppliers come from with
// their default lead time, and the store-level planning defaults. The same
// controls Store settings renders (`components/settings/`), so the two
// screens cannot say different things about the same fields; the copy under
// each control is theirs too. The layout and its spacing are this popup's own
// (`StoreSetup.css`).
//
// The form starts from the values the entry read served (`step`: the setup
// part of `GET /api/store/entry/`, the store's stored values) and never from
// a default of its own — read strictly, the way Store settings reads its
// payload (`pages/storeSettingsForms.js`). The answer goes to
// `POST /api/store/setup/` as the `planning`, `sourcing` and `catalog` blocks
// (`shopify_app.views.saas.store_setup`); on success the caller re-reads the
// entry, which then reports the step answered.
//
// A refused field (`body.errors`, keyed `planning.<field>`) is shown on its
// control; the structured `rebuild_unavailable` and `import_unavailable`
// refusals have their own copy, anything else reads as the generic failure.
// Both leave the step owed, so Continue is the retry.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button } from '@mui/material';

import { extractPlanningForm, extractSourcingForm } from '../../pages/storeSettingsForms';
import CategorySourceSelect from '../settings/CategorySourceSelect';
import {
  InventoryTargetFields, LeadTimeField, OrderCadenceFields,
} from '../settings/PlanningDefaultsFields';
import SupplierSourceSelect from '../settings/SupplierSourceSelect';
import { planningDefaultsPayload, planningDefaultsValid } from '../settings/planningDefaults';
import EntryDialog from './EntryDialog';
import './StoreSetup.css';

const PLANNING_ERROR_PREFIX = 'planning.';
// Structured codes the answer can come back with (shopify_app/views/saas.py
// store_setup), each with its own copy.
const ERROR_KEYS = new Set(['rebuild_unavailable', 'import_unavailable']);

const describeError = (t, code) => (
  code && ERROR_KEYS.has(code) ? t(`storeSetup.errors.${code}`) : t('storeSetup.errors.generic')
);

// The `planning.*` half of a 400's per-field error map, re-keyed by bare
// field name so each message lands on the control that produced it.
const planningFieldErrors = (errors) => Object.fromEntries(
  Object.entries(errors)
    .filter(([key]) => key.startsWith(PLANNING_ERROR_PREFIX))
    .map(([key, message]) => [key.slice(PLANNING_ERROR_PREFIX.length), message]),
);

function Section({ title, children }) {
  return (
    <section className="store-setup__section">
      <h3 className="store-setup__title">{title}</h3>
      {children}
    </section>
  );
}

export default function StoreSetup({ step, onAnswer, onAnswered }) {
  const { t } = useTranslation();
  // `catalog` is the category switch's state; the stored source is the value
  // the picker starts on (a rebuild cannot be in flight before the store has
  // opened, so there is no pending target to prefer).
  const [planning, setPlanning] = useState(() => extractPlanningForm(step.planning));
  const [sourcing, setSourcing] = useState(() => extractSourcingForm(step.sourcing));
  const [categorySource, setCategorySource] = useState(step.catalog.category_source);
  const [planningErrors, setPlanningErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const editPlanning = (patch) => {
    setPlanning((form) => ({ ...form, ...patch }));
    // A server message describes the value that was SENT; the moment the
    // merchant changes that field it no longer applies.
    setPlanningErrors((errors) => Object.fromEntries(
      Object.entries(errors).filter(([field]) => !(field in patch)),
    ));
  };

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onAnswer({
        planning: planningDefaultsPayload(planning),
        sourcing,
        catalog: { category_source: categorySource },
      });
      onAnswered();
    } catch (err) {
      console.error('Store setup answer failed:', err?.message || err);
      const errors = err?.body?.errors;
      if (errors && typeof errors === 'object') {
        setPlanningErrors(planningFieldErrors(errors));
        setError(t('storeSetup.errors.checkFields'));
      } else {
        setError(describeError(t, err?.code ?? null));
      }
      setSubmitting(false);
    }
  };

  return (
    <EntryDialog
      title={t('storeSetup.heading')}
      size="standard"
      className="store-setup"
      actions={(
        <Button
          variant="contained"
          onClick={submit}
          loading={submitting}
          disabled={submitting || !planningDefaultsValid(planning)}
        >
          {t('storeSetup.continue')}
        </Button>
      )}
    >
      <p className="store-setup__intro">{t('storeSetup.body')}</p>

      <Section title={t('storeSetup.productsSection')}>
        <CategorySourceSelect
          value={categorySource}
          onChange={(e) => setCategorySource(e.target.value)}
        />
      </Section>

      <Section title={t('storeSetup.suppliersSection')}>
        <div className="store-setup__suppliers">
          <SupplierSourceSelect
            value={sourcing.supplier_source}
            onChange={(e) => setSourcing({ supplier_source: e.target.value })}
          />
          <LeadTimeField form={planning} onChange={editPlanning} errors={planningErrors} />
        </div>
      </Section>

      <Section title={t('storeSetup.planningSection')}>
        <div className="store-setup__planning">
          <InventoryTargetFields form={planning} onChange={editPlanning} errors={planningErrors} />
          <OrderCadenceFields form={planning} onChange={editPlanning} errors={planningErrors} />
        </div>
      </Section>

      {error && <Alert severity="error">{error}</Alert>}
    </EntryDialog>
  );
}
